import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { FiSearch, FiCheckCircle, FiArrowRight, FiHome, FiMapPin, FiAward, FiCalendar } from 'react-icons/fi';
import Header from '../components/Header';
import PropertyCard from '../components/PropertyCard';
import WhatsAppButton from '../components/WhatsAppButton';
import Footer from '../components/layout/Footer';
import GoogleReviews from '../components/GoogleReviews';
import ExploreLocations from '../components/location/ExploreLocations';
import {
  propertyService,
  buildImageUrl,
  categoryService,
  statsService,
  contentService,
  blogService,
} from '../services/api';
import { Property, PropertyFilterOptions, PropertyShowcaseCollection } from '../types';
import { useSettings } from '../contexts/SettingsContext';
import Seo, { organizationSchema } from '../components/Seo';
import {
  Container,
  Section,
  SectionHeader,
  Eyebrow,
  SectionTitle,
  SectionSubtitle,
  Button,
  Input,
  Select,
  Skeleton,
} from '../components/ui';
import { formatPropertyCount } from '../utils/propertySearchFilters';

interface SearchFilters {
  city: string;
  neighborhood: string;
  condominium: string;
  transaction_type: string;
  type: string;
  minPrice: string;
  maxPrice: string;
  bedrooms: string;
}

interface Category {
  id: number;
  name: string;
  slug: string;
  description?: string;
  color: string;
  icon?: string;
  is_active: boolean;
  is_featured: boolean;
  order_index: number;
  properties?: Property[];
}

interface ServiceItem {
  title: string;
  content: string;
  link: string;
  link_label: string;
}

interface BlogPostSummary {
  slug: string;
  title: string;
  excerpt?: string;
  featured_image?: string;
  published_at: string;
}

interface HomeShowcaseCollection extends PropertyShowcaseCollection {
  href?: string;
}

interface PriceOption {
  value: string;
  label: string;
}

const SALE_PRICE_OPTIONS: { min: PriceOption[]; max: PriceOption[] } = {
  min: [
    { value: '100000', label: 'R$ 100 mil' },
    { value: '300000', label: 'R$ 300 mil' },
    { value: '500000', label: 'R$ 500 mil' },
    { value: '1000000', label: 'R$ 1 milhão' },
  ],
  max: [
    { value: '300000', label: 'R$ 300 mil' },
    { value: '600000', label: 'R$ 600 mil' },
    { value: '1000000', label: 'R$ 1 milhão' },
    { value: '2000000', label: 'R$ 2 milhões' },
  ],
};

const RENTAL_PRICE_OPTIONS: { min: PriceOption[]; max: PriceOption[] } = {
  min: [
    { value: '1000', label: 'R$ 1 mil/mês' },
    { value: '2000', label: 'R$ 2 mil/mês' },
    { value: '3000', label: 'R$ 3 mil/mês' },
    { value: '5000', label: 'R$ 5 mil/mês' },
  ],
  max: [
    { value: '2000', label: 'R$ 2 mil/mês' },
    { value: '3000', label: 'R$ 3 mil/mês' },
    { value: '5000', label: 'R$ 5 mil/mês' },
    { value: '10000', label: 'R$ 10 mil/mês' },
  ],
};

const getPriceOptions = (transactionType: string) => {
  if (transactionType === 'aluguel') return RENTAL_PRICE_OPTIONS;
  return SALE_PRICE_OPTIONS;
};

const extractSearchItems = (response: any): Property[] => {
  if (Array.isArray(response)) return response;
  return Array.isArray(response?.items) ? response.items : [];
};

const extractSearchTotal = (response: any, items: Property[]) => {
  return typeof response?.total === 'number' ? response.total : items.length;
};

const EMPTY_FILTER_OPTIONS: PropertyFilterOptions = {
  cities: [],
  neighborhoods: [],
  condominiums: [],
};

const Page = styled.div`
  min-height: 100vh;
  background: ${({ theme }) => theme.colors.background};
`;

/* --------------------------------- Hero -------------------------------- */

const Hero = styled.section<{ $bg: string }>`
  position: relative;
  padding: 5rem 0 4.5rem;
  background-image: linear-gradient(
      to bottom,
      rgba(15, 23, 42, 0.62),
      rgba(15, 23, 42, 0.72)
    ),
    url(${({ $bg }) => $bg});
  background-size: cover;
  background-position: center;
  color: #fff;

  @media (max-width: 768px) {
    padding: 3.5rem 0 3rem;
  }
`;

const HeroInner = styled(Container)`
  max-width: 920px;
`;

const HeroEyebrow = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  font-size: 0.85rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: #fff;
  background: rgba(255, 255, 255, 0.14);
  border: 1px solid rgba(255, 255, 255, 0.22);
  padding: 0.4rem 0.85rem;
  border-radius: 999px;
  margin-bottom: 1.25rem;
`;

const HeroTitle = styled.h1`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(2.2rem, 5vw, 3.5rem);
  font-weight: 700;
  line-height: 1.08;
  letter-spacing: -0.02em;
  max-width: 18ch;
  color: #fff;
`;

const HeroSubtitle = styled.p`
  margin-top: 1.1rem;
  font-size: clamp(1.02rem, 2vw, 1.2rem);
  line-height: 1.6;
  color: rgba(255, 255, 255, 0.88);
  max-width: 54ch;
`;

const SearchCard = styled.form`
  margin-top: 2rem;
  background: ${({ theme }) => theme.colors.card};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.overlay};
  padding: 1rem;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 0.6rem;
  align-items: end;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }
  @media (max-width: 520px) {
    grid-template-columns: 1fr;
  }
`;

const SCField = styled.label`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  color: ${({ theme }) => theme.colors.neutral[500]};
`;

const TrustRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1.25rem 2rem;
  margin-top: 1.75rem;

  span {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.92rem;
    color: rgba(255, 255, 255, 0.9);
  }
  svg {
    color: #fff;
  }
`;

/* --------------------------------- Stats ------------------------------- */

const StatsBand = styled.div`
  background: ${({ theme }) => theme.colors.card};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
`;

const StatsGrid = styled(Container)`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.5rem;
  padding-top: 2.5rem;
  padding-bottom: 2.5rem;

  @media (max-width: 720px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 1.75rem 1rem;
  }
`;

const StatItem = styled.div`
  text-align: center;
`;

const StatNumber = styled.div`
  font-family: ${({ theme }) => theme.fonts.heading};
  font-size: clamp(1.8rem, 4vw, 2.5rem);
  font-weight: 700;
  color: ${({ theme }) => theme.colors.primary};
  line-height: 1;
`;

const StatLabel = styled.div`
  margin-top: 0.5rem;
  font-size: 0.9rem;
  color: ${({ theme }) => theme.colors.text.secondary};
`;

/* ------------------------------ Properties ----------------------------- */

const Grid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;

  @media (max-width: 980px) {
    grid-template-columns: repeat(2, 1fr);
  }
  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const CenterCta = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 2.5rem;
`;

/* ------------------------------ Categories ----------------------------- */

const CategoryBlock = styled.div`
  margin-bottom: 3rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const CategoryHead = styled(Link)<{ $color?: string }>`
  display: flex;
  align-items: center;
  gap: 0.9rem;
  margin-bottom: 1.5rem;

  .mark {
    width: 46px;
    height: 46px;
    border-radius: ${({ theme }) => theme.borderRadius.md};
    background: ${({ $color, theme }) => ($color || theme.colors.primary) + '1a'};
    color: ${({ $color, theme }) => $color || theme.colors.primary};
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }
  h3 {
    font-family: ${({ theme }) => theme.fonts.heading};
    font-size: 1.35rem;
    font-weight: 600;
    color: ${({ theme }) => theme.colors.foreground};
  }
  p {
    font-size: 0.9rem;
    color: ${({ theme }) => theme.colors.text.secondary};
  }
`;

const ViewAllLink = styled(Link)<{ $color?: string }>`
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  color: ${({ $color, theme }) => $color || theme.colors.primary};
  font-weight: 600;
  font-size: 0.95rem;
  margin-top: 1.5rem;

  svg {
    transition: transform 0.2s ease;
  }
  &:hover svg {
    transform: translateX(3px);
  }
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 1.25rem;
  @media (max-width: 980px) { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  @media (max-width: 560px) { grid-template-columns: 1fr; }
`;

const ServiceCard = styled.div`
  padding: 1.5rem;
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  box-shadow: ${({ theme }) => theme.shadows.card};
  .icon { width: 44px; height: 44px; display: grid; place-items: center; color: ${({ theme }) => theme.colors.primary}; background: ${({ theme }) => theme.colors.primarySoft}; border-radius: ${({ theme }) => theme.borderRadius.md}; }
  h3 { margin: 1rem 0 .65rem; font-size: 1.15rem; text-transform: capitalize; }
  p { color: ${({ theme }) => theme.colors.text.secondary}; line-height: 1.65; font-size: .93rem; }
  a { display: inline-flex; align-items: center; gap: .35rem; margin-top: 1rem; color: ${({ theme }) => theme.colors.primary}; font-weight: 600; }
`;

const NewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1.5rem;
  @media (max-width: 800px) { grid-template-columns: 1fr; }
`;

const NewsCard = styled(Link)`
  overflow: hidden;
  background: ${({ theme }) => theme.colors.card};
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.borderRadius.lg};
  color: ${({ theme }) => theme.colors.foreground};
  img { width: 100%; aspect-ratio: 16/9; object-fit: cover; }
  .body { padding: 1.2rem; }
  .date { display: flex; align-items: center; gap: .35rem; color: ${({ theme }) => theme.colors.text.secondary}; font-size: .8rem; }
  h3 { margin-top: .65rem; line-height: 1.4; }
`;

const HomePage: React.FC = () => {
  const { siteSettings, loading: settingsLoading } = useSettings();
  const [featuredCategories, setFeaturedCategories] = useState<Category[]>([]);
  const [showcaseCollections, setShowcaseCollections] = useState<HomeShowcaseCollection[]>([]);
  const [systemStats, setSystemStats] = useState<any>(null);
  const [services, setServices] = useState<ServiceItem[]>([]);
  const [blogPosts, setBlogPosts] = useState<BlogPostSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterOptions, setFilterOptions] = useState<PropertyFilterOptions>(EMPTY_FILTER_OPTIONS);
  const [filters, setFilters] = useState<SearchFilters>({
    city: '',
    neighborhood: '',
    condominium: '',
    transaction_type: '',
    type: '',
    minPrice: '',
    maxPrice: '',
    bedrooms: '',
  });
  const navigate = useNavigate();

  useEffect(() => {
    const loadData = async () => {
      if (settingsLoading || !siteSettings) return;
      try {
        setLoading(true);
        const [servicesResponse, blogResponse, categoriesResponse, filterOptionsResponse] = await Promise.allSettled([
          contentService.getByKey('homepage_services'),
          blogService.getPosts({ limit: 3 }),
          categoryService.getFeatured(),
          propertyService.getFilterOptions(),
        ]);
        if (servicesResponse.status === 'fulfilled') {
          setServices(servicesResponse.value?.data?.items || []);
        }
        if (blogResponse.status === 'fulfilled') {
          setBlogPosts(blogResponse.value?.items || []);
        }
        if (filterOptionsResponse.status === 'fulfilled') {
          setFilterOptions(filterOptionsResponse.value);
        } else {
          console.error('Erro ao carregar opções de localização:', filterOptionsResponse.reason);
        }
        if (siteSettings.use_real_stats) {
          try {
            const statsResponse = await statsService.getSystemStats();
            setSystemStats(statsResponse);
          } catch (error) {
            console.error('Erro ao carregar estatísticas do sistema:', error);
          }
        }

        let editorialCategories: Category[] = [];
        if (categoriesResponse.status === 'fulfilled' && Array.isArray(categoriesResponse.value)) {
          editorialCategories = categoriesResponse.value;
        } else if (categoriesResponse.status === 'rejected') {
          console.error('Erro ao carregar categorias em destaque:', categoriesResponse.reason);
        }

        const loadDynamicShowcase = async () => {
          setFeaturedCategories([]);
          try {
            const showcase = await propertyService.getShowcase(6);
            const dynamicCollections = [showcase?.highlights, showcase?.more_opportunities].filter(
              (collection): collection is PropertyShowcaseCollection =>
                Boolean(collection && Array.isArray(collection.items))
            );

            if (dynamicCollections.length !== 2) {
              throw new Error('Resposta incompleta da vitrine de imóveis');
            }
            setShowcaseCollections(dynamicCollections);
          } catch (showcaseError) {
            console.error('Erro ao carregar vitrine de imóveis:', showcaseError);
            try {
              // Backends anteriores ignoram `collection`. Nesse caso duas buscas
              // rotulariam os mesmos imóveis como Destaques e Mais oportunidades.
              // Sem o contrato atômico de /showcase, exibimos uma única seleção
              // neutra e nunca inventamos o vínculo com o Canal Pro.
              const fallbackResponse = await propertyService.search({
                status: 'disponivel',
                limit: 6,
              });
              const fallbackItems = extractSearchItems(fallbackResponse);
              setShowcaseCollections([{
                key: 'mais-oportunidades',
                title: 'Imóveis disponíveis',
                subtitle: 'Explore as opções disponíveis no momento.',
                total: extractSearchTotal(fallbackResponse, fallbackItems),
                items: fallbackItems,
                href: '/imoveis',
              }]);
            } catch (fallbackError) {
              console.error('Erro ao carregar fallback da vitrine:', fallbackError);
              setShowcaseCollections([]);
            }
          }
        };

        if (editorialCategories.length > 0) {
          const categoriesWithProperties = await Promise.all(
            editorialCategories.map(async (category: Category) => {
              try {
                const propertiesResponse = await propertyService.search({
                  category_id: category.id,
                  status: 'disponivel',
                  limit: 3,
                });
                return { ...category, properties: extractSearchItems(propertiesResponse) };
              } catch (error) {
                console.error(`Erro ao carregar imóveis da categoria ${category.id}:`, error);
                return { ...category, properties: [] };
              }
            })
          );
          const populatedCategories = categoriesWithProperties.filter(
            (category) => (category.properties?.length || 0) > 0
          );
          if (populatedCategories.length > 0) {
            setFeaturedCategories(populatedCategories);
            setShowcaseCollections([]);
          } else {
            await loadDynamicShowcase();
          }
        } else {
          await loadDynamicShowcase();
        }
      } catch (error) {
        console.error('Erro ao carregar dados:', error);
      } finally {
        setLoading(false);
      }
    };

    if (!settingsLoading && siteSettings) {
      loadData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsLoading]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    const searchParams = new URLSearchParams();
    if (filters.city) searchParams.set('city', filters.city);
    if (filters.neighborhood) searchParams.set('neighborhood', filters.neighborhood);
    if (filters.condominium) searchParams.set('condominium', filters.condominium);
    if (filters.transaction_type) searchParams.set('transaction_type', filters.transaction_type);
    if (filters.type) searchParams.set('type', filters.type);
    if (filters.transaction_type && filters.minPrice) searchParams.set('min_price', filters.minPrice);
    if (filters.transaction_type && filters.maxPrice) searchParams.set('max_price', filters.maxPrice);
    if (filters.bedrooms) searchParams.set('bedrooms', filters.bedrooms);
    navigate(`/imoveis?${searchParams.toString()}`);
  };

  const handleFilterChange = (field: keyof SearchFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [field]: value }));

  const handleTransactionTypeChange = (value: string) => {
    setFilters((prev) => ({
      ...prev,
      transaction_type: value,
      minPrice: '',
      maxPrice: '',
    }));
  };

  const priceOptions = getPriceOptions(filters.transaction_type);
  const priceFiltersEnabled = Boolean(filters.transaction_type);

  const stats = {
    totalProperties:
      siteSettings?.use_real_stats && systemStats
        ? systemStats.total_properties
        : siteSettings?.total_properties || 1250,
    activeCities:
      siteSettings?.use_real_stats && systemStats
        ? systemStats.active_cities
        : siteSettings?.active_cities || 45,
    happyClients: siteSettings?.happy_clients || 5800,
    yearsExperience: siteSettings?.years_experience || 15,
  };

  const companyName = siteSettings?.company_info?.name || siteSettings?.site_name || 'nossa imobiliária';
  const heroTitle =
    siteSettings?.customization?.hero_title || 'Encontre o imóvel ideal para você';
  const heroSubtitle =
    siteSettings?.customization?.hero_subtitle ||
    `Mais de ${stats.totalProperties.toLocaleString('pt-BR')} imóveis em ${stats.activeCities} cidades. Compre, venda ou alugue com assessoria completa.`;
  const heroImage = siteSettings?.customization?.hero_image
    ? buildImageUrl(siteSettings.customization.hero_image)
    : 'https://images.unsplash.com/photo-1560518883-ce09059eeffa?auto=format&fit=crop&w=1973&q=80';

  const homepageSections = siteSettings?.homepage_sections || [
    { id: 'hero', name: 'Hero', enabled: true, order: 1 },
    { id: 'stats', name: 'Estatísticas', enabled: true, order: 2 },
    { id: 'categories', name: 'Categorias', enabled: true, order: 3 },
    { id: 'properties', name: 'Imóveis', enabled: true, order: 4 },
    { id: 'reviews', name: 'Avaliações', enabled: true, order: 5 },
  ];
  const enabledSections = homepageSections
    .filter((s: any) => s.enabled)
    .sort((a: any, b: any) => a.order - b.order);

  const renderSection = (id: string) => {
    switch (id) {
      case 'hero':
        return (
          <Hero key="hero" $bg={heroImage}>
            <HeroInner>
              <HeroEyebrow>
                <FiMapPin size={14} /> {companyName}
              </HeroEyebrow>
              <HeroTitle>{heroTitle}</HeroTitle>
              <HeroSubtitle>{heroSubtitle}</HeroSubtitle>

              <SearchCard onSubmit={handleSearch} aria-label="Busca de imóveis">
                <SCField>
                  Cidade
                  <Input
                    type="text"
                    placeholder="Onde quer morar?"
                    aria-label="Cidade"
                    list="home-property-cities"
                    value={filters.city}
                    onChange={(e) => handleFilterChange('city', e.target.value)}
                  />
                </SCField>
                <SCField>
                  Bairro
                  <Input
                    type="text"
                    placeholder="Todos os bairros"
                    aria-label="Bairro"
                    list="home-property-neighborhoods"
                    value={filters.neighborhood}
                    onChange={(e) => handleFilterChange('neighborhood', e.target.value)}
                  />
                </SCField>
                <SCField>
                  Condomínio
                  <Input
                    type="text"
                    placeholder="Todos os condomínios"
                    aria-label="Condomínio ou empreendimento"
                    list="home-property-condominiums"
                    value={filters.condominium}
                    onChange={(e) => handleFilterChange('condominium', e.target.value)}
                  />
                </SCField>
                <SCField>
                  Finalidade
                  <Select
                    value={filters.transaction_type}
                    onChange={(e) => handleTransactionTypeChange(e.target.value)}
                    aria-label="Finalidade"
                  >
                    <option value="">Comprar ou alugar</option>
                    <option value="venda">Comprar</option>
                    <option value="aluguel">Alugar</option>
                  </Select>
                </SCField>
                <SCField>
                  Tipo
                  <Select aria-label="Tipo de imóvel" value={filters.type} onChange={(e) => handleFilterChange('type', e.target.value)}>
                    <option value="">Todos</option>
                    <option value="apartamento">Apartamento</option>
                    <option value="casa">Casa</option>
                    <option value="terreno">Terreno</option>
                    <option value="comercial">Comercial</option>
                  </Select>
                </SCField>
                <SCField>
                  Preço mín.
                  <Select
                    aria-label={priceFiltersEnabled ? 'Preço mínimo' : 'Escolha Comprar ou Alugar antes do preço mínimo'}
                    value={filters.minPrice}
                    onChange={(e) => handleFilterChange('minPrice', e.target.value)}
                    disabled={!priceFiltersEnabled}
                  >
                    <option value="">{priceFiltersEnabled ? 'Qualquer' : 'Escolha a finalidade'}</option>
                    {priceFiltersEnabled && priceOptions.min.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Select>
                </SCField>
                <SCField>
                  Preço máx.
                  <Select
                    aria-label={priceFiltersEnabled ? 'Preço máximo' : 'Escolha Comprar ou Alugar antes do preço máximo'}
                    value={filters.maxPrice}
                    onChange={(e) => handleFilterChange('maxPrice', e.target.value)}
                    disabled={!priceFiltersEnabled}
                  >
                    <option value="">{priceFiltersEnabled ? 'Qualquer' : 'Escolha a finalidade'}</option>
                    {priceFiltersEnabled && priceOptions.max.map((option) => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </Select>
                </SCField>
                <SCField>
                  Quartos
                  <Select aria-label="Quantidade mínima de quartos" value={filters.bedrooms} onChange={(e) => handleFilterChange('bedrooms', e.target.value)}>
                    <option value="">Qualquer</option>
                    <option value="1">1+</option>
                    <option value="2">2+</option>
                    <option value="3">3+</option>
                    <option value="4">4+</option>
                  </Select>
                </SCField>
                <Button type="submit" $variant="primary" $size="lg" aria-label="Buscar imóveis com os filtros selecionados">
                  <FiSearch size={18} /> Buscar
                </Button>
                <datalist id="home-property-cities">
                  {filterOptions.cities.map((option) => (
                    <option key={option.value} value={option.value}>
                      {formatPropertyCount(option.count)}
                    </option>
                  ))}
                </datalist>
                <datalist id="home-property-neighborhoods">
                  {filterOptions.neighborhoods.map((option) => (
                    <option key={option.value} value={option.value}>
                      {formatPropertyCount(option.count)}
                    </option>
                  ))}
                </datalist>
                <datalist id="home-property-condominiums">
                  {filterOptions.condominiums.map((option) => (
                    <option key={option.value} value={option.value}>
                      {formatPropertyCount(option.count)}
                    </option>
                  ))}
                </datalist>
              </SearchCard>

              <TrustRow>
                <span>
                  <FiCheckCircle size={18} /> Imóveis verificados
                </span>
                <span>
                  <FiAward size={18} /> {stats.yearsExperience}+ anos de experiência
                </span>
                <span>
                  <FiCheckCircle size={18} /> Assessoria completa
                </span>
              </TrustRow>
            </HeroInner>
          </Hero>
        );

      case 'stats':
        return (
          <StatsBand key="stats">
            <StatsGrid>
              <StatItem>
                <StatNumber>{stats.totalProperties.toLocaleString('pt-BR')}</StatNumber>
                <StatLabel>Imóveis disponíveis</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{stats.activeCities}</StatNumber>
                <StatLabel>Cidades atendidas</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{stats.happyClients.toLocaleString('pt-BR')}</StatNumber>
                <StatLabel>Clientes satisfeitos</StatLabel>
              </StatItem>
              <StatItem>
                <StatNumber>{stats.yearsExperience}</StatNumber>
                <StatLabel>Anos de experiência</StatLabel>
              </StatItem>
            </StatsGrid>
          </StatsBand>
        );

      case 'categories':
        return featuredCategories && featuredCategories.length > 0 ? (
          <Section key="categories" $muted>
            <Container>
              <SectionHeader>
                <Eyebrow>Categorias</Eyebrow>
                <SectionTitle>Explore por categoria</SectionTitle>
                <SectionSubtitle>
                  Seleções especiais para você encontrar mais rápido o que procura.
                </SectionSubtitle>
              </SectionHeader>

              {featuredCategories.map((category) => (
                <CategoryBlock key={category.id}>
                  <CategoryHead to={`/imoveis?category_id=${category.id}`} $color={category.color}>
                    <span className="mark">
                      <FiHome size={22} />
                    </span>
                    <div>
                      <h3>{category.name}</h3>
                      {category.description && <p>{category.description}</p>}
                    </div>
                  </CategoryHead>

                  {category.properties && category.properties.length > 0 && (
                    <>
                      <Grid>
                        {category.properties.map((property) => (
                          <PropertyCard key={property.id} property={property} />
                        ))}
                      </Grid>
                      <ViewAllLink to={`/imoveis?category_id=${category.id}`} $color={category.color}>
                        Ver todos de {category.name} <FiArrowRight size={16} />
                      </ViewAllLink>
                    </>
                  )}
                </CategoryBlock>
              ))}
            </Container>
          </Section>
        ) : null;

      case 'properties':
        return !featuredCategories || featuredCategories.length === 0 ? (
          <Section key="properties">
            <Container>
              <SectionHeader>
                <Eyebrow>Imóveis</Eyebrow>
                <SectionTitle>Encontre sua próxima oportunidade</SectionTitle>
                <SectionSubtitle>
                  Seleções atualizadas para você encontrar mais rápido o que procura.
                </SectionSubtitle>
              </SectionHeader>

              {loading ? (
                <Grid>
                  {Array.from({ length: 6 }).map((_, i) => (
                    <div key={i}>
                      <Skeleton $h="0" style={{ aspectRatio: '4/3' }} $radius="12px" />
                      <Skeleton $h="1.1rem" $w="80%" style={{ marginTop: '0.8rem' }} />
                      <Skeleton $h="0.85rem" $w="55%" style={{ marginTop: '0.6rem' }} />
                    </div>
                  ))}
                </Grid>
              ) : showcaseCollections.length > 0 ? (
                showcaseCollections.map((collection) => (
                  <CategoryBlock key={collection.key}>
                    <CategoryHead to={collection.href || `/imoveis?collection=${collection.key}`}>
                      <span className="mark">
                        {collection.key === 'destaques' ? <FiAward size={22} /> : <FiHome size={22} />}
                      </span>
                      <div>
                        <h3>{collection.title}</h3>
                        <p>{collection.subtitle}</p>
                      </div>
                    </CategoryHead>

                    {collection.items.length > 0 ? (
                      <Grid>
                        {collection.items.map((property) => (
                          <PropertyCard key={property.id} property={property} />
                        ))}
                      </Grid>
                    ) : (
                      <SectionSubtitle>Nenhum imóvel nesta seleção no momento.</SectionSubtitle>
                    )}

                    <ViewAllLink to={collection.href || `/imoveis?collection=${collection.key}`}>
                      {collection.href ? 'Ver todos os imóveis' : `Ver todos em ${collection.title}`}{' '}
                      <FiArrowRight size={16} />
                    </ViewAllLink>
                  </CategoryBlock>
                ))
              ) : (
                <SectionSubtitle>Nenhum imóvel disponível no momento.</SectionSubtitle>
              )}
            </Container>
          </Section>
        ) : null;

      case 'reviews':
        return (
          <GoogleReviews
            key="reviews"
            limit={6}
            minRating={4}
            showStats={true}
            title="O que nossos clientes dizem"
            subtitle="Depoimentos reais de quem confiou no nosso trabalho"
          />
        );

      case 'services':
        return services.length > 0 ? (
          <Section key="services" $muted>
            <Container>
              <SectionHeader>
                <Eyebrow>Nosso propósito</Eyebrow>
                <SectionTitle>Oferecer as melhores opções e negócios no imóvel dos seus sonhos</SectionTitle>
              </SectionHeader>
              <ServiceGrid>
                {services.map((service) => (
                  <ServiceCard key={service.title}>
                    <div className="icon"><FiHome size={21} /></div>
                    <h3>{service.title}</h3>
                    <p>{service.content}</p>
                    <Link to={service.link}>{service.link_label} <FiArrowRight /></Link>
                  </ServiceCard>
                ))}
              </ServiceGrid>
            </Container>
          </Section>
        ) : null;

      case 'blog':
        return blogPosts.length > 0 ? (
          <Section key="blog">
            <Container>
              <SectionHeader>
                <Eyebrow>Novidades</Eyebrow>
                <SectionTitle>Conteúdo para fazer bons negócios</SectionTitle>
              </SectionHeader>
              <NewsGrid>
                {blogPosts.map((post) => (
                  <NewsCard key={post.slug} to={`/blog/${post.slug}`}>
                    {post.featured_image && <img src={buildImageUrl(post.featured_image)} alt={post.title} />}
                    <div className="body">
                      <span className="date"><FiCalendar /> {new Date(post.published_at).toLocaleDateString('pt-BR')}</span>
                      <h3>{post.title}</h3>
                    </div>
                  </NewsCard>
                ))}
              </NewsGrid>
              <CenterCta><Button as={Link} to="/blog" $variant="outline">Ver todos os artigos</Button></CenterCta>
            </Container>
          </Section>
        ) : null;

      default:
        return null;
    }
  };

  return (
    <Page>
      <Seo
        title={siteSettings?.settings?.meta_title || siteSettings?.site_name || companyName}
        description={siteSettings?.settings?.meta_description || siteSettings?.site_description || heroSubtitle}
        canonicalPath="/"
        image={heroImage}
        structuredData={organizationSchema(siteSettings)}
      />
      <Header />
      <WhatsAppButton />
      {enabledSections.map((section: any) => renderSection(section.id)).filter(Boolean)}
      <ExploreLocations />
      <Footer />
    </Page>
  );
};

export default HomePage;
